import type { LightboxItem } from '@/components/elements/ImageLightbox/ImageLightbox';
import { imageHqSrc, imageSrc } from '@/lib/imageUrl';
import type { GalleryCategoryData, GalleryItemData, SanityImageData } from '@/types/sanity';

export const LOCAL_GALLERY_URL = '/galleryimg.json';
export const FALLBACK_ALT = 'Waterproofing work by Sheel Waterproofing';

/** Responsive variants for a local image, produced by `npm run generate-gallery`. */
export interface LocalImageVariants {
  srcSet?: string;
  /** Full-quality file, only fetched when the image is opened in the viewer. */
  hqSrc?: string;
  width?: number;
  height?: number;
  lqip?: string;
}

/**
 * A category, wherever it came from. Sanity items carry the real `galleryCategory` document
 * (`_id`, `title`, `slug`); a local item only ever has a title and a slug computed from it
 * (see generateGalleryJSON.js) — it has no Sanity document to reference, so `_id` is absent.
 */
export type MediaCategory = Omit<GalleryCategoryData, '_id'> & { _id?: string };

/**
 * One tile in the grid. CMS images carry Sanity data; local ones are plain URLs. `title` is
 * only ever a caption someone actually typed on the Sanity document (the Title field is
 * optional there, and the local manifest has no title field at all) — never a designated /
 * derived fallback (not a filename, not the item's category), so a photo shows a caption only
 * when a person deliberately wrote one.
 */
export type MediaItem =
  | { id: string; kind: 'sanity-image'; image: SanityImageData; alt: string; category: MediaCategory | null; title: string | null }
  | ({ id: string; kind: 'image'; src: string; alt: string; category: MediaCategory | null; title: string | null } & LocalImageVariants)
  | { id: string; kind: 'video'; src: string; category: MediaCategory | null; title: string | null };

export type VideoItem = Extract<MediaItem, { kind: 'video' }>;

/** An image that a category-group tile can show in its coverflow (see CategoryCoverflow.tsx). */
export type CoverflowImage = Extract<MediaItem, { kind: 'sanity-image' | 'image' }>;

/**
 * A stand-in for every item in a category, shown on the "All Works" page as its own full-width
 * carousel section (see CategoryCarouselSection.tsx) instead of flooding the masonry grid with
 * every one of them individually. Clicking the active photo goes to that category's own page
 * (see GalleryCategoryPage), which lists them all. `images` only ever holds the group's photos,
 * not its videos — a category whose items are all videos just shows those videos individually
 * instead (see Gallery.tsx).
 */
export interface CategoryGroupTile {
  id: string;
  kind: 'category-group';
  slug: string;
  title: string;
  /** Optional; shown next to the title on the carousel (see CategoryCarouselSection.tsx). */
  description?: string | null;
  count: number;
  images: CoverflowImage[];
}

/** Shape of an entry in `public/galleryimg.json` (see generateGalleryJSON.js). */
interface LocalGalleryEntry extends LocalImageVariants {
  id: number;
  type: 'image' | 'video';
  src: string;
  category: MediaCategory | null;
}

export async function fetchLocalGallery(signal: AbortSignal): Promise<LocalGalleryEntry[]> {
  const response = await fetch(LOCAL_GALLERY_URL, { signal });
  if (!response.ok) {
    throw new Error(`Failed to fetch ${LOCAL_GALLERY_URL}: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

const timestamp = (value: string | null): number => (value ? Date.parse(value) || 0 : 0);

/** Manually ordered items first (ascending `order`), everything else newest first. */
export function compareGalleryItems(a: GalleryItemData, b: GalleryItemData): number {
  const aOrdered = a.order !== null;
  const bOrdered = b.order !== null;
  if (aOrdered && bOrdered && a.order !== b.order) return a.order! - b.order!;
  if (aOrdered !== bOrdered) return aOrdered ? -1 : 1;
  return timestamp(b.uploadedAt) - timestamp(a.uploadedAt);
}

/** "Title, photo 3": distinct for every image, so each one is described on its own. */
function describePhoto(title: string | null, index: number): string {
  return `${title || FALLBACK_ALT}, photo ${index + 1}`;
}

export function toSanityMedia(item: GalleryItemData): MediaItem[] {
  return (item.mediaFiles ?? []).flatMap((file, index): MediaItem[] => {
    const id = `sanity-${item._id}-${index}`;

    if (file._type === 'image') {
      return file.asset
        ? [{ id, kind: 'sanity-image', image: file, alt: describePhoto(item.title, index), category: item.category, title: item.title }]
        : [];
    }

    return file.url && file.mimeType?.startsWith('video/')
      ? [{ id, kind: 'video', src: file.url, category: item.category, title: item.title }]
      : [];
  });
}

export function toLocalMedia(entry: LocalGalleryEntry): MediaItem {
  const id = `local-${entry.id}`;
  // The local manifest has no per-photo title field at all (see generateGalleryJSON.js) —
  // captions only ever come from a title someone typed in Sanity, so a local photo never
  // shows one.
  if (entry.type === 'video') return { id, kind: 'video', src: entry.src, category: entry.category, title: null };

  const { src, srcSet, hqSrc, width, height, lqip, category } = entry;
  return { id, kind: 'image', alt: describePhoto(null, entry.id), src, srcSet, hqSrc, width, height, lqip, category, title: null };
}

/** Width of the compressed image shown instantly in the viewer (a size the grid already requests). */
const VIEWER_BASE_WIDTH = 960;

/** Images open in the viewer; videos have their own expand behaviour. */
export function toLightboxItem(item: MediaItem): LightboxItem | null {
  if (item.kind === 'sanity-image') {
    const { image } = item;
    return {
      id: item.id,
      alt: item.alt,
      src: imageSrc(image, VIEWER_BASE_WIDTH),
      hqSrc: imageHqSrc(image),
      width: image.dimensions?.width,
      height: image.dimensions?.height,
      lqip: image.lqip,
    };
  }

  if (item.kind === 'image') {
    return {
      id: item.id,
      alt: item.alt,
      src: item.src,
      hqSrc: item.hqSrc,
      width: item.width,
      height: item.height,
      lqip: item.lqip,
    };
  }

  return null;
}
